import { ReactElement, useEffect, useRef, useState } from 'react';

import { AnchorLink } from '@displate/styleguide';
// eslint-disable-next-line import/no-extraneous-dependencies
import Vimeo from '@vimeo/player';
import clsx from 'clsx';

import { trackGA4ViewItemList } from '@/common/analytics/gtm/item/trackViewItemList';
import { trackElementInteraction } from '@/common/analytics/gtm/shared/trackElementInteraction';
import { trackElementImpression } from '@/common/analytics/gtm/trackElementImpression';
import { ACRONYMS, EventMessage, LISTING_FUNCTIONS } from '@/common/analytics/gtm/types';
import Avatar from '@/common/components/Avatar/Avatar';
import { FollowButton } from '@/common/components/FollowButton/FollowButton';
import SliderWithImpressions from '@/common/components/Slider/SliderWithImpressions';
import SliderArtworkItemWithAnalytics from '@/common/components/SliderArtworkItem/SliderArtworkItemWithAnalytics';
import VimeoPlayer from '@/common/components/VimeoPlayer/VimeoPlayer';
import { StaticImage } from '@/common/constants/displateRoutes';
import { useOnScreen } from '@/common/hooks/useOnScreen/useOnScreen';
import { ImpressionsContextProvider } from '@/common/impressions';
import { useCurrentPage } from '@/common/routes/ElysiumKnownPages';
import { RouteToArtistPage } from '@/common/routes/ExternalRoutes';
import { Loader } from '@/common/styleguide/atoms/Loader/Loader';
import { HomepageConfig } from '@/modules/HomePage/HomePageConfig';
import { HomepageBrand, HomepageGalleryItem } from 'generatedBfw/static';

import styles from './brandOfTheWeek.module.scss';

interface Props {
  brand: HomepageBrand;
  artworks: HomepageGalleryItem[];
}

const BrandOfTheWeekView = ({ brand, artworks }: Props): ReactElement => {
  const [isInViewport, setIsInViewport] = useState(false);
  const [isPlayerEnabled, setIsPlayerEnabled] = useState(false);
  const [isLoaderEnabled, setIsLoaderEnabled] = useState(true);
  const videoRef = useRef<HTMLDivElement | null>(null);
  const brandOfTheWeekContainerRef = useRef<HTMLDivElement | null>(null);
  const playerRef = useRef<Vimeo>();
  const isDocumentVisible = useRef(true);
  const isPlayerInUntouchedState = useRef(true);
  const pageConfig = useCurrentPage();
  const pageType = pageConfig.analyticsGaName;

  useEffect(() => {
    if (!playerRef.current) {
      return;
    }

    playerRef.current.on('pause', () => {
      if (!document.hidden) {
        // do not trigger interaction when user changes tabs (loses focus on the video tab)
        isDocumentVisible.current = true;
      }

      isDocumentVisible.current &&
        trackElementInteraction({
          elementId: 'brand_of_the_week_video',
          elementVariant: 'pause',
        });
      if (isPlayerInUntouchedState.current) {
        isPlayerInUntouchedState.current = false;
      }
    });

    playerRef.current.on('play', () => {
      !isPlayerInUntouchedState.current &&
        isDocumentVisible.current &&
        trackElementInteraction({
          elementId: 'brand_of_the_week_video',
          elementVariant: 'play',
        });

      if (!document.hidden) {
        // do not trigger interaction when user changes tabs (loses focus on the video tab)
        isDocumentVisible.current = false;
      }
    });

    return () => {
      playerRef.current && playerRef.current.off('play');
      playerRef.current && playerRef.current.off('pause');
    };
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [playerRef.current]);

  const handleOnReady = (player: Vimeo) => {
    playerRef.current = player;
    setIsLoaderEnabled(false);
  };

  useOnScreen(brandOfTheWeekContainerRef, {
    once: true,
    delay: 1000,
    callback: () => {
      trackElementImpression({
        elementId: 'brand_of_the_week',
      });
    },
  });

  // trigger video play when video is in viewport
  useOnScreen(videoRef, {
    once: true,
    delay: 100,
    callback: () => {
      setIsInViewport(true);
    },
    afterDelayCallback: true,
    onHold: videoRef.current === null,
  });

  // trigger video component load when there's 300px left to see it
  useOnScreen(brandOfTheWeekContainerRef, {
    once: true,
    threshold: 0,
    rootMargin: '300px',
    callback: () => {
      setIsPlayerEnabled(true);
    },
  });

  return (
    <section
      className={styles.container}
      data-testid="brand-of-the-week"
      ref={brandOfTheWeekContainerRef}
    >
      <div className={styles.brandPreview} data-testid="brand-of-the-week--preview">
        <div className={styles.overviewWrapper}>
          <div className={styles.overview}>
            <div className={styles.laurel} data-testid="brand-of-the-week--laurel">
              <img
                alt="left laurel"
                height={34}
                loading="lazy"
                src={StaticImage('/icons/homepage/laur_left.svg')}
                width={18}
              />
              Brand of the month
              <img
                alt="right laurel"
                height={34}
                loading="lazy"
                src={StaticImage('/icons/homepage/laur_right.svg')}
                width={18}
              />
            </div>
            <div className={styles.overviewDetails} data-testid="brand-of-the-week--details">
              <Avatar
                author={{ ...brand, isLicensed: true }}
                className={styles.avatar}
                onClick={() => trackElementInteraction({ elementId: 'brand_of_the_week_logo' })}
              />
              <div>
                <h2 className={styles.name} data-testid="brand-of-the-week--name">
                  {brand.name}
                </h2>
                <div className={styles.stats} data-testid="brand-of-the-week--stats">
                  {brand.artworksCount > 0 && (
                    <span
                      className={styles.statsCollections}
                      data-testid="brand-of-the-week--stats-collections"
                    >
                      {brand.collectionsCount} collection{brand.collectionsCount > 1 ? `s` : ''}
                    </span>
                  )}
                  {brand.artworksCount > 0 && (
                    <span
                      className={styles.statsArtworks}
                      data-testid="brand-of-the-week--stats-artworks"
                    >
                      {brand.artworksCount} artwork{brand.artworksCount > 1 ? `s` : ''}
                    </span>
                  )}
                </div>
              </div>
            </div>
            <p className={styles.description} data-testid="brand-of-the-week--description">
              {brand.description}
            </p>
            <div className={styles.actions} data-testid="brand-of-the-week--actions">
              <FollowButton
                authorId={brand.userId}
                metadata={{
                  pageType,
                  componentLocation: 'featured_brand',
                  authorName: brand.name,
                  listingFunction: LISTING_FUNCTIONS.FOLLOW,
                }}
              />
              <AnchorLink
                className={styles.link}
                data-testid="brand-of-the-week--link"
                href={RouteToArtistPage({ licensed: true, nick: brand.slug }).route}
                onClick={() => trackElementInteraction({ elementId: 'brand_of_the_week_link' })}
              >
                Visit brand shop
              </AnchorLink>
            </div>
          </div>
        </div>
        <div
          className={styles.video}
          data-testid="brand-of-the-week--video-container"
          ref={videoRef}
        >
          {isPlayerEnabled ? (
            <VimeoPlayer
              className={styles.brandOfTheWeekVimeo}
              onReady={handleOnReady}
              paused={!isInViewport}
              video={brand.videoId}
            />
          ) : isLoaderEnabled && !playerRef.current ? (
            <div
              className={clsx(styles.brandOfTheWeekVimeo, styles.videoLoaderContainer, {
                [styles.videoLoaderContainerAbsolute]: !!playerRef.current,
              })}
            >
              <Loader className={styles.videoLoader} />
            </div>
          ) : null}
        </div>
      </div>
      <div className={styles.brandFeed} data-testid="brand-of-the-week--feed">
        <ImpressionsContextProvider<EventMessage> callback={trackGA4ViewItemList}>
          <SliderWithImpressions
            keyExtractor={(item) => item.internalId}
            metadata={{ elementVariant: 'brand_of_the_week' }}
            renderSlide={(item, index, useSlideImpression) => (
              <SliderArtworkItemWithAnalytics
                className={styles.artworkItem}
                index={index}
                item={{ ...item, imageCoreUrl: `${item.imageCoreUrl}.jpg` }}
                metadata={{
                  athenaPageName: HomepageConfig.analyticsAthenaName,
                  gaPageName: HomepageConfig.analyticsGaName,
                  itemListName: brand.name,
                  listingFunction: ACRONYMS.BRAND_OF_THE_WEEK,
                  listingType: 'slider',
                }}
                useSlideImpression={useSlideImpression}
              />
            )}
            slideClassName={styles.slide}
            slides={artworks}
          />
        </ImpressionsContextProvider>
      </div>
    </section>
  );
};

export default BrandOfTheWeekView;
